/**
 * Commercial plan definitions.
 *
 * These constants are the single source of truth for quota enforcement across
 * the API, the web client and the mobile client. The API is always the
 * authority — clients use these values only to render accurate copy and to
 * pre-empt obviously-doomed requests.
 */

export const PLAN = Object.freeze({
  FREE: 'free',
  PRO: 'pro',
});

export const PLANS = Object.freeze([PLAN.FREE, PLAN.PRO]);

/**
 * `null` means unlimited. Never use a sentinel number such as 999999 —
 * downstream code distinguishes "no limit" from "a very large limit".
 */
export const PLAN_LIMITS = Object.freeze({
  free: Object.freeze({
    playlists: 1,
    songsPerPlaylist: 15,
    customSongs: 5,
    /**
     * Lessons a Free learner may complete per day. Generous enough for a real
     * daily habit, low enough that finishing the free units is a matter of
     * weeks. The daily challenge is deliberately NOT counted against it.
     */
    learnLessonsPerDay: 3,
  }),
  pro: Object.freeze({
    playlists: null,
    songsPerPlaylist: null,
    customSongs: null,
    learnLessonsPerDay: null,
  }),
});

/**
 * The single RevenueCat entitlement identifier shared by every platform.
 *
 * This is the *identifier* configured in the RevenueCat dashboard, which for
 * this project contains spaces. It is legal and it is deliberate — but it means
 * this constant must match the dashboard byte for byte, because it is the
 * fallback every client and the API use when `REVENUECAT_ENTITLEMENT_ID` is
 * absent from the environment. The old value here was `pro`, which resolved
 * every real subscriber to Free while warning only to a console nobody reads in
 * a release build. Do not "tidy" it back.
 */
export const ENTITLEMENT_ID = 'ChordBook Pro Unlimited';

/**
 * Subscription lifecycle states.
 *
 * Derived from the stored entitlement + expiry data, never from a webhook
 * event name alone (event names describe what happened, not current access).
 */
export const SUBSCRIPTION_STATUS = Object.freeze({
  FREE: 'free',
  TRIALING: 'trialing',
  ACTIVE: 'active',
  GRACE_PERIOD: 'grace_period',
  BILLING_ISSUE: 'billing_issue',
  CANCELED_ACTIVE: 'canceled_active',
  EXPIRED: 'expired',
  PROMOTIONAL: 'promotional',
  UNKNOWN: 'unknown',
});

export const SUBSCRIPTION_STATUSES = Object.freeze(Object.values(SUBSCRIPTION_STATUS));

/**
 * Statuses that grant Premium access.
 *
 * `unknown` is deliberately excluded: when we cannot verify entitlement we
 * preserve read access but refuse mutations that would exceed Free limits.
 */
export const PRO_STATUSES = Object.freeze([
  SUBSCRIPTION_STATUS.TRIALING,
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.GRACE_PERIOD,
  SUBSCRIPTION_STATUS.CANCELED_ACTIVE,
  SUBSCRIPTION_STATUS.PROMOTIONAL,
]);

export const SUBSCRIPTION_SOURCE = Object.freeze({
  APP_STORE: 'app_store',
  PLAY_STORE: 'play_store',
  WEB: 'web',
  PROMOTIONAL: 'promotional',
  NONE: 'none',
});

export const SUBSCRIPTION_SOURCES = Object.freeze(Object.values(SUBSCRIPTION_SOURCE));

/** Feature identifiers used by `hasFeature(user, feature)` and paywall routing. */
export const FEATURE = Object.freeze({
  UNLIMITED_PLAYLISTS: 'unlimited_playlists',
  UNLIMITED_PLAYLIST_SONGS: 'unlimited_playlist_songs',
  UNLIMITED_CUSTOM_SONGS: 'unlimited_custom_songs',
  OFFLINE_CATALOG: 'offline_catalog',
  ADVANCED_EDITOR: 'advanced_editor',
  BULK_PLAYLIST_ACTIONS: 'bulk_playlist_actions',
  CHORDPRO_IMPORT_EXPORT: 'chordpro_import_export',
  PLAIN_TEXT_IMPORT: 'plain_text_import',
  REVISION_HISTORY: 'revision_history',
  MULTIPLE_ARRANGEMENT_VERSIONS: 'multiple_arrangement_versions',
  DRAFT_SYNC: 'draft_sync',
  LEARN_FULL_CURRICULUM: 'learn_full_curriculum',
  LEARN_UNLIMITED_PRACTICE: 'learn_unlimited_practice',

  /**
   * Every tuning in the tuner beyond standard six-string: the other presets,
   * seven- and eight-string instruments, and setting each string by hand.
   *
   * Deliberately one identifier rather than three. Those three capabilities are
   * always granted or withheld together, so three ids would be three ways to
   * say the same thing — and the paywall would render three locked rows for one
   * capability. The marketing bullets ("7 & 8 string support") are paywall copy,
   * driven by the context, not by this enum.
   */
  TUNER_ALTERNATE_TUNINGS: 'tuner_alternate_tunings',
});

export const FEATURES = Object.freeze(Object.values(FEATURE));

/** Features unlocked by the `pro` entitlement. Everything else is available to all users. */
export const PRO_FEATURES = Object.freeze([
  FEATURE.UNLIMITED_PLAYLISTS,
  FEATURE.UNLIMITED_PLAYLIST_SONGS,
  FEATURE.UNLIMITED_CUSTOM_SONGS,
  FEATURE.OFFLINE_CATALOG,
  FEATURE.ADVANCED_EDITOR,
  FEATURE.BULK_PLAYLIST_ACTIONS,
  FEATURE.CHORDPRO_IMPORT_EXPORT,
  FEATURE.PLAIN_TEXT_IMPORT,
  FEATURE.REVISION_HISTORY,
  FEATURE.MULTIPLE_ARRANGEMENT_VERSIONS,
  FEATURE.DRAFT_SYNC,
  FEATURE.LEARN_FULL_CURRICULUM,
  FEATURE.LEARN_UNLIMITED_PRACTICE,
  FEATURE.TUNER_ALTERNATE_TUNINGS,
]);

/** Contextual paywall variants. Each maps to tailored, non-coercive copy. */
export const PAYWALL_CONTEXT = Object.freeze({
  GENERAL_UPGRADE: 'general_upgrade',
  PLAYLIST_LIMIT: 'playlist_limit',
  PLAYLIST_SONG_LIMIT: 'playlist_song_limit',
  CUSTOM_SONG_LIMIT: 'custom_song_limit',
  OFFLINE_REQUIRED: 'offline_required',
  ADVANCED_EDITOR_REQUIRED: 'advanced_editor_required',
  REVISION_HISTORY_REQUIRED: 'revision_history_required',
  LEARN_UNIT_LOCKED: 'learn_unit_locked',
  LEARN_DAILY_LIMIT: 'learn_daily_limit',
  TUNER_TUNING_LOCKED: 'tuner_tuning_locked',
});

export const PAYWALL_CONTEXTS = Object.freeze(Object.values(PAYWALL_CONTEXT));

/** Introductory trial length, in days. The billing platform remains authoritative. */
export const TRIAL_DAYS = 7;

/**
 * How long a locally-stored entitlement snapshot is trusted before the API
 * refreshes it from RevenueCat. Keeps a single failed network request from
 * downgrading a paying customer.
 */
export const ENTITLEMENT_STALE_AFTER_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Additional access window retained after a confirmed expiry, covering store
 * reporting delays. Offline catalog downloads survive this window.
 */
export const ENTITLEMENT_GRACE_PERIOD_MS = 48 * 60 * 60 * 1000; // 48 hours

/** Returns the quota object for a plan, defaulting to Free for unknown input. */
export function getPlanLimits(plan) {
  return PLAN_LIMITS[plan] ?? PLAN_LIMITS[PLAN.FREE];
}

/** True when the plan has no limit for the given quota key. */
export function isUnlimited(plan, key) {
  return getPlanLimits(plan)[key] === null;
}

/** True when the status grants Premium entitlement. */
export function statusGrantsPro(status) {
  return PRO_STATUSES.includes(status);
}

/** Plan implied by a subscription status. */
export function planForStatus(status) {
  return statusGrantsPro(status) ? PLAN.PRO : PLAN.FREE;
}

/** True when `feature` requires an active Premium entitlement. */
export function isProFeature(feature) {
  return PRO_FEATURES.includes(feature);
}

/**
 * Remaining allowance for a quota.
 * Returns `null` for unlimited, otherwise a non-negative integer.
 */
export function remainingQuota(plan, key, used) {
  const limit = getPlanLimits(plan)[key];
  if (limit === null || limit === undefined) return null;
  return Math.max(0, limit - (Number(used) || 0));
}

/** True when a further item may be created without exceeding the plan quota. */
export function withinQuota(plan, key, used) {
  const limit = getPlanLimits(plan)[key];
  if (limit === null || limit === undefined) return true;
  return (Number(used) || 0) < limit;
}
